import React from 'react';
import { StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import {
  AnimatedDialogScrollViewWrapper,
  AnimatedDialogScrollViewScroll,
  theme,
  webOnlyStyles,
} from '@sg/garnish';

import { useFloatingBottomBarSpacing } from '@order/shared-hooks';
import { useUsableNetBannerOffset } from '@order/usable-net';

import { BagUseCardButton } from '../../Ctas';

// ─────────────────────────────────────────────────────────────────────────────

export const BagPaymentElementContainer = (props: Props) => {
  const {
    children,
    isShowingPaymentElement,
    isAddingNewPaymentMethod,
    isChangingPaymentMethod,
    isPaymentSetupComplete,
    onUseCard,
  } = props;

  // ─── Full Height Payment Element ───────────────────────────────────────────
  // On web only, the payment element is shown as full height, native doesn't need it.

  const usableNetBannerOffset = useUsableNetBannerOffset();
  const fullHeightMinusUsableNet = `calc(100svh - ${usableNetBannerOffset}px)` as never;
  const paymentElementsHeight = webOnlyStyles({ maxHeight: fullHeightMinusUsableNet });
  const paymentElementDialog = isShowingPaymentElement ? paymentElementsHeight : undefined;
  const paymentElementsStyle = [styles.stripePaymentElement, paymentElementDialog];

  // ─── Floating Payment Element Button ───────────────────────────────────────
  // On web only, there is an use card button that is shown as a floating button at the bottom of the screen.

  const { floatingBottomBarSpacing } = useFloatingBottomBarSpacing();
  const showUseCardButton = isShowingPaymentElement && isAddingNewPaymentMethod; // prettier-ignore
  const paymentElementsPaddingBottom = showUseCardButton ? theme.spacing['16'] + floatingBottomBarSpacing : 0; // prettier-ignore
  const paymentElementsContentStyle = useStyle(() => ({ paddingBottom: paymentElementsPaddingBottom }), [paymentElementsPaddingBottom]); // prettier-ignore

  // ──────────────────────────────────────────────────────────────────────────

  return (
    <AnimatedDialogScrollViewWrapper lockFocus={isShowingPaymentElement}>
      <AnimatedDialogScrollViewScroll
        show={isChangingPaymentMethod}
        contentContainerStyle={paymentElementsContentStyle}
        style={isChangingPaymentMethod ? paymentElementsStyle : styles.hiddenWrapper}
        pointerEvents={isChangingPaymentMethod ? 'auto' : 'none'}
        accessibilityElementsHidden={!isChangingPaymentMethod}
      >
        {children}
      </AnimatedDialogScrollViewScroll>

      {showUseCardButton ? (
        <BagUseCardButton disabled={!isPaymentSetupComplete} onPress={onUseCard} />
      ) : null}
    </AnimatedDialogScrollViewWrapper>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  stripePaymentElement: {
    maxHeight: 500,
  },
  hiddenWrapper: {
    flex: 0,
    maxHeight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  },
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  children: React.ReactNode;
  isShowingPaymentElement: boolean;
  isAddingNewPaymentMethod: boolean;
  isChangingPaymentMethod: boolean;
  isPaymentSetupComplete: boolean;
  onUseCard: () => void;
};
